// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { montecarloApi } from '@advisor-app/store/api/ai/montecarlo-api';
import { portfolioApi } from '@shared/data-access/api/ai/portfolio-api';
import { type AssetsInfoResp } from '@shared/data-access/interfaces/api/ai/assets';
import { type EsgAnalysis } from '@shared/data-access/interfaces/api/ai/esg-analysis';
import {
  type MontecarloSimulationPayload,
  type MontecarloSimulationResp,
} from '@shared/data-access/interfaces/api/ai/montecarlo';
import { type PortfolioBacktest } from '@shared/data-access/interfaces/api/ai/portfolio';
import {
  type EtfAnalysis,
  type PortfolioAnalysis,
  type RiskReturns as RiskReturnsAnalysis,
  type StockAnalysis,
} from '@shared/data-access/interfaces/api/ai/portfolio-analysis';
import { type PortfolioStrategy } from '@shared/data-access/interfaces/api/ai/portfolio-strategy';
import { type TargetStrategy } from '@shared/data-access/interfaces/api/ai/target-strategy';
import { type TheoreticalTrade } from '@shared/data-access/interfaces/api/ai/theoretical-trade';
import axios from 'axios';
import { HIST_VALUES } from '@shared/ui-kit/components/period-picker';
import { downloadReports } from '@advisor-app/features/utils/download-reports';
import { generatePayloadOptimization } from '@advisor-app/features/utils/generate-payload-optimization';
import { type PortfolioPositionsCurrent } from '@shared/data-access/interfaces/redux/portfolio-state';
import { useSafeUpdate } from '@shared/ui-kit/hooks';
import { useHandleApiErrors } from '@advisor-app/hooks/use-handle-api-errors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  appStore as store,
  useAppDispatch,
  useAppSelector,
} from '@advisor-app/store/store';
import { setCopilotInsightPayload } from '@advisor-app/store/slices/copilot-buffer';
import {
  selectBenchmark,
  selectHistoricalWindow,
  setHistoricalWindow,
} from '@advisor-app/store/slices/parameters';
import {
  portfolioPositionsSlice,
  selectCurrentBalance,
} from '@shared/data-access/slices/portfolio-positions';
import {
  selectWorkflowAsset,
  selectWorkflowType,
} from '@advisor-app/store/slices/workflow';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import { trackPromise } from 'react-promise-tracker';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { RTK_PROMISE_TRACKER_ID } from '@shared/data-access/utils/constants';
import { useTranslation } from 'react-i18next';

interface PositionsDataResp {
  backtest: PortfolioBacktest | null;
  analysis: PortfolioAnalysis | null;
  esgAnalysis: EsgAnalysis | null;
  assetsInfo: AssetsInfoResp[] | null;
  simulation: MontecarloSimulationResp[] | null;
}

interface useGetPositionsProps {
  holdings: Array<PortfolioPositionsCurrent & { outstanding_balance: number }>;
}

export const useGetPositions = ({ holdings }: useGetPositionsProps) => {
  const { t } = useTranslation();
  const handleApiErrors = useHandleApiErrors();
  const runSafeUpdate = useSafeUpdate();
  const dispatch = useAppDispatch();
  const assetType = useAppSelector(selectWorkflowAsset);
  const hist = useAppSelector(selectHistoricalWindow);
  const currency = useAppSelector(selectAccountCurrency);
  const defaultHist = useMemo(() => HIST_VALUES.get(hist), [hist]);
  const setHist = useCallback(
    (value: number) => dispatch(setHistoricalWindow(value)),
    [dispatch],
  );
  const benchmark = useAppSelector(state => selectBenchmark(state, 'ai-builder'));
  const [wasOptimized, setWasOptimized] = useState<boolean>(false);
  const [portfolioStrategy, setPortfolioStrategy] = useState<PortfolioStrategy>({
    benchmark: {
      target_asset: benchmark,
    },
    holdings: holdings.map(({ harvesting: _, ...holding }) => holding),
  });
  const [targetStrategy, setTargetStrategy] = useState<TargetStrategy>({
    target_asset: [],
  });

  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(true);
  const [isLoadingPositions, setIsLoadingPositions] = useState(true);
  const [isGettingAnalysis, setIsGettingAnalysis] = useState(false);
  const [responses, setResponses] = useState<PositionsDataResp>({
    backtest: null,
    analysis: null,
    esgAnalysis: null,
    assetsInfo: null,
    simulation: null,
  });

  const currentBalance = useAppSelector(selectCurrentBalance);

  const { payloadOptimization } = useMemo(
    () => generatePayloadOptimization(store),
    [],
  );

  const workflowType = useAppSelector(selectWorkflowType);

  const [getAssetsInfo] = assetsApi.useLazyAssetsInfoQuery();
  const [getOptimization] = portfolioApi.useLazyPortfolioOptimizationQuery();
  const [getBacktest] = portfolioApi.useLazyPortfolioBacktestQuery();
  const [getAnalysis] = portfolioApi.useLazyPortfolioAnalysisQuery();
  const [getStockAnalysisEsg] =
    portfolioApi.useLazyPortfolioStockAnalysisEsgQuery();
  const [montecarloSimulation] = montecarloApi.useLazySimulationQuery();
  const [triggerTrading] = portfolioApi.useLazyRebalanceTradingQuery();

  const getAnalysisData = useCallback(async () => {
    setIsGettingAnalysis(true);
    /* istanbul ignore next */
    try {
      const {
        data: analysisAssetsInfo,
        error: analysisAssetsInfoError,
        isError: isAnalysisAssetsInfoError,
      } = await getAssetsInfo(
        {
          payload: portfolioStrategy.holdings.map(({ ticker }) => ticker),
          params: { detail: true },
          assetType,
        },
        true,
      );

      if (isAnalysisAssetsInfoError) throw analysisAssetsInfoError;

      const { data: analysisData } = await getAnalysis(
        {
          payload: portfolioStrategy,
          params: { hist_w: hist },
          assetType,
        },
        true,
      );

      const { data: backtestData } = await getBacktest(
        {
          payload: portfolioStrategy,
          params: { hist_w: hist },
          assetType,
        },
        true,
      );

      const analysisResp: PortfolioAnalysis = {
        ...(analysisData as PortfolioAnalysis),
        risk_returns: (analysisData as PortfolioAnalysis)?.risk_returns?.map(
          item => ({
            ...item,
            name: analysisAssetsInfo?.find(asset => asset.ticker === item.ticker)
              ?.name,
          }),
        ),
      };

      let esgAnalysisResp: EsgAnalysis | null = null;
      let simulationResp: MontecarloSimulationResp[] | null = null;

      const tickersEsg: string[] = [];
      const tickersRisk: string[] = [];

      (analysisResp as StockAnalysis).top_holdings?.target_asset?.forEach(asset =>
        tickersRisk.push(asset?.ticker),
      );

      if (assetType === 'stock') {
        try {
          const esgData = await getStockAnalysisEsg({
            payload: portfolioStrategy,
          }).unwrap();

          esgAnalysisResp = esgData as EsgAnalysis;

          esgAnalysisResp?.top_assets?.map(asset => tickersEsg.push(asset?.ticker));
        } catch (error) {
          console.error('[getAnalysisData]: stockAnalysisEsg', error);
        }
      }

      if (['etf', 'mutual_fund'].includes(assetType)) {
        (analysisResp as EtfAnalysis)?.top_holdings?.forEach(holding =>
          tickersRisk.push(holding.ticker),
        );
      }

      const {
        data: assetsInfoData,
        error: assetsInfoError,
        isError: isAssetsInfoError,
      } = await getAssetsInfo(
        {
          payload: [...tickersEsg, ...tickersRisk],
          params: { detail: true },
        },
        true,
      );

      if (isAssetsInfoError) throw assetsInfoError;

      const riskReturnPortfolio = analysisResp?.risk_returns.find(
        (rr: RiskReturnsAnalysis) => rr.ticker === 'Portfolio',
      );

      const simulationPayload: MontecarloSimulationPayload = {
        expected_returns: riskReturnPortfolio?.returns as number,
        expected_volatility: riskReturnPortfolio?.volatility as number,
        agg_period: 1,
        initial_amount: currentBalance,
        cashflow_contribution: 0,
        n_periods: 20,
        n_scenarios: 2000,
        quantiles: [0.1, 0.25, 0.5, 0.75, 0.9],
        risk_free_rate: 2.5 / 100,
      };

      try {
        simulationResp = await montecarloSimulation({
          payload: simulationPayload,
        }).unwrap();
      } catch (error) {
        console.error('[getAnalysisData]: montecarloSimulation', error);
      }

      runSafeUpdate(() => {
        setResponses({
          ...responses,
          analysis: analysisResp!,
          backtest: backtestData!,
          esgAnalysis: esgAnalysisResp,
          assetsInfo: assetsInfoData!,
          simulation: simulationResp,
        });

        dispatch(
          setCopilotInsightPayload({
            portfolio: analysisResp,
            ...(simulationResp && {
              simulation: {
                montecarlo_settings: simulationPayload,
                montecarlo_quantiles: simulationResp,
              },
            }),
          }),
        );

        setIsLoadingAnalysis(false);
      });
    } catch (error: any) {
      handleApiErrors(error);
      console.error('[useGetPositions]:', error);
    }
    setIsGettingAnalysis(false);
  }, [
    portfolioStrategy,
    hist,
    assetType,
    getAssetsInfo,
    getBacktest,
    getAnalysis,
    getStockAnalysisEsg,
    currentBalance,
    runSafeUpdate,
    responses,
    dispatch,
    handleApiErrors,
    montecarloSimulation,
  ]);

  const getPositions = async () => {
    /* istanbul ignore next */
    try {
      let tickersTrades: string[];
      let outstandingBalanceTrades: number[];
      let unrealizedCapitalGainsTrades: number[];
      let theoreticalTrades: TheoreticalTrade[] = [];

      if (
        workflowType !== 'load' &&
        workflowType !== 'asset-allocation' &&
        workflowType !== 'research'
      ) {
        const rebalanceTrades = payloadOptimization.trades.filter(
          trade => trade.ticker !== '$CASH',
        );

        const tradesResp = await triggerTrading({
          payload: {
            holdings,
            trades: rebalanceTrades,
            target_strategy: targetStrategy,
          },
        }).unwrap();

        const totalTheoreticalTrades = tradesResp
          .concat(payloadOptimization.trades)
          .sort((a, b) => b.volume - a.volume)
          .filter(trade => trade.ticker !== '$CASH');

        tickersTrades = totalTheoreticalTrades.map(trade => trade.ticker);
        outstandingBalanceTrades = totalTheoreticalTrades.map(
          trade => trade.volume,
        );
        unrealizedCapitalGainsTrades = totalTheoreticalTrades.map(
          trade => trade.unrealized_capital_gains,
        );
        theoreticalTrades = totalTheoreticalTrades;
      } else {
        tickersTrades = holdings.map(holding => holding.ticker);
        unrealizedCapitalGainsTrades = holdings.map(
          holding => holding.unrealized_capital_gains,
        );
        outstandingBalanceTrades = holdings.map(
          holding => holding.outstanding_balance,
        );
      }

      const {
        data: positionsResp,
        error,
        isError,
      } = await getAssetsInfo(
        {
          payload: tickersTrades,
          params: { detail: true },
          assetType,
        },
        true,
      );

      if (isError) throw error;

      const positionsWithBalance = positionsResp!.map(position => {
        const index = tickersTrades.indexOf(position.ticker);

        return {
          ...position,
          outstanding_balance: outstandingBalanceTrades[index],
          unrealized_capital_gains: unrealizedCapitalGainsTrades[index],
          weight: (
            (outstandingBalanceTrades[index] * 100) /
            currentBalance
          ).toFixed(2),
        };
      });

      let completedPositions = positionsWithBalance;

      if (theoreticalTrades.length) {
        completedPositions = positionsWithBalance.map(data => {
          const trade = theoreticalTrades.find(t => t.ticker === data.ticker);
          return trade ? { ...data, ...trade } : data;
        });
      }

      if (
        workflowType !== 'load' &&
        workflowType !== 'asset-allocation' &&
        workflowType !== 'research'
      ) {
        const rebalanceTargetStrategy = targetStrategy.target_asset.map(
          ({ ticker, weight }) => ({ ticker, weight }),
        );

        runSafeUpdate(() => {
          dispatch(
            portfolioPositionsSlice.actions.setTargetPortfolioPositions(
              rebalanceTargetStrategy,
            ),
          );
        });
      }

      runSafeUpdate(() => {
        dispatch(
          portfolioPositionsSlice.actions.setTradesPortfolioPositions(
            payloadOptimization.trades,
          ),
        );
        dispatch(
          portfolioPositionsSlice.actions.setTradesTablePortfolioPositions(
            completedPositions as any[],
          ),
        );

        setIsLoadingPositions(false);
      });
    } catch (error) {
      console.error('[useGetPositions]:', error);
    }
  };

  const runOptimization = useCallback(async () => {
    /* istanbul ignore next */
    try {
      const { data: optimization } = await getOptimization({
        payload: payloadOptimization,
        params: { hist_w: hist, opt_global: 'True' },
        assetType,
      });

      runSafeUpdate(() => {
        setTargetStrategy(optimization![1]);
        setWasOptimized(true);
        setPortfolioStrategy(optimization![0]);
      });
    } catch (e) {
      if (axios.isAxiosError(e)) handleApiErrors(e);
    }
  }, [
    assetType,
    handleApiErrors,
    hist,
    payloadOptimization,
    runSafeUpdate,
    getOptimization,
  ]);

  const handleDownloadReport = useCallback(() => {
    downloadReports(
      {
        portfolio_analysis: responses.analysis as StockAnalysis,
        esg_analysis: responses.esgAnalysis as EsgAnalysis,
        portfolio_investment: portfolioStrategy,
        portfolio_backtest: responses.backtest!,
        assetType,
      },
      dispatch,
      t,
    );
  }, [
    responses.analysis,
    responses.esgAnalysis,
    responses.backtest,
    portfolioStrategy,
    assetType,
    dispatch,
    t,
  ]);

  const handleDeleteTradePosition = useCallback(
    (ticker: string) => {
      const { tradesTable } = store.getState().portfolioPositions;
      const index = tradesTable.findIndex(p => p.ticker === ticker);
      if (index !== -1) {
        dispatch(portfolioPositionsSlice.actions.deleteTradesTablePosition(index));
      }
      setPortfolioStrategy(prev => ({
        ...prev,
        holdings: holdings.filter(holding => ticker !== holding.ticker),
      }));

      if (workflowType !== 'load' && workflowType !== 'asset-allocation')
        runOptimization();
    },
    [dispatch, workflowType, runOptimization, holdings],
  );

  useEffect(() => {
    if (!['load', 'asset-allocation', 'research'].includes(workflowType))
      runOptimization();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      ['load', 'asset-allocation', 'research'].includes(workflowType) ||
      (wasOptimized && portfolioStrategy.holdings.length > 0)
    ) {
      getAnalysisData();
      trackPromise(getPositions(), RTK_PROMISE_TRACKER_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portfolioStrategy, hist, currency]);

  return {
    isLoadingPositions,
    isLoadingAnalysis,
    isGettingAnalysis,
    handleDownloadReport,
    setHist,
    defaultHist,
    handleDeleteTradePosition,
    ...responses,
  };
};
